/**
 * Iframe / popup content-height reporting for hosts.
 *
 * When `report_size=true` is present, this module measures `.view`'s
 * content height (falling back to `#app`) and posts updates to the host
 * (`window.opener` for popups, otherwise `window.parent` for iframes).
 *
 * ## Enabling
 *
 * ```
 * ?report_size=true
 * ```
 *
 * ## Message format
 *
 * ```ts
 * { type: "resize", height: number }
 * ```
 *
 * ## Host example
 *
 * ```js
 * window.addEventListener("message", (event) => {
 *   if (event.data?.type === "resize") {
 *     iframe.style.height = `${event.data.height}px`;
 *   }
 * });
 * ```
 */

const RESIZE_MESSAGE_TYPE = "resize";
const REPORT_SIZE_QUERY_PARAM = "report_size";

function isQueryFlagEnabled(name: string): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) === "true";
}

/** Whether the current page URL opts into publishing content height. */
export function isReportSizeEnabled(): boolean {
  return isQueryFlagEnabled(REPORT_SIZE_QUERY_PARAM);
}

/**
 * Content height of `.view` (or `#app`).
 *
 * `.view` is content-sized (not stretched to the shell), so scrollHeight
 * is the height of its contents. Auto margins on `.view` live outside
 * its box and are not included.
 */
function measureContentHeight(): number {
  const target =
    (document.querySelector(".view") as HTMLElement | null) ||
    (document.getElementById("app") as HTMLElement | null);

  if (!target) {
    return 0;
  }

  return Math.ceil(target.scrollHeight);
}

/** Host window that should receive resize messages (opener or parent). */
function getHostWindow(): Window | null {
  if (window.opener && !window.opener.closed) {
    return window.opener;
  }
  if (window.parent && window.parent !== window) {
    return window.parent;
  }
  return null;
}

/** Publishes the current content height to the host window. */
function postHeight(height: number): void {
  const host = getHostWindow();
  if (!host) {
    return;
  }

  host.postMessage(
    {
      type: RESIZE_MESSAGE_TYPE,
      height,
    },
    "*",
  );
}

/**
 * Starts observing content size changes and posts `resize` messages
 * to the host whenever the measured height changes.
 *
 * No-ops unless {@link isReportSizeEnabled} is true.
 */
export function startResizeReporter() {
  if (!isReportSizeEnabled()) {
    return;
  }

  let lastPostedHeight = 0;

  const report = () => {
    const height = measureContentHeight();
    if (height === lastPostedHeight) {
      return;
    }
    lastPostedHeight = height;
    postHeight(height);
  };

  const resizeObserver = new ResizeObserver(() => {
    report();
  });

  const viewElement = document.querySelector(".view");
  const appElement = document.getElementById("app");
  const observeTarget = viewElement || appElement;

  if (observeTarget) {
    resizeObserver.observe(observeTarget);
    for (const child of observeTarget.children) {
      resizeObserver.observe(child);
    }
  }

  // Template content often mounts after the first paint.
  const mutationObserver = observeTarget
    ? new MutationObserver(() => {
        if (viewElement) {
          for (const child of viewElement.children) {
            resizeObserver.observe(child);
          }
        }
        report();
      })
    : null;

  mutationObserver?.observe(observeTarget as Element, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Catch late layout shifts (fonts, images, async content).
  window.addEventListener("load", report);
  window.addEventListener("resize", report);

  report();

  return () => {
    resizeObserver.disconnect();
    mutationObserver?.disconnect();
    window.removeEventListener("load", report);
    window.removeEventListener("resize", report);
  };
}
